import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Check, X, Bell, FileText, Eye, User, Lock, ShieldCheck, Database, Menu } from 'lucide-react';
import SEO from '../components/SEO';
import MediaCarousel from '../components/MediaCarousel';
import SocialLinks from '../components/SocialLinks';
import { trackLandingVisit } from '../utils/visitTracker';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";

const IMG = {
  hero: "/images/how-it-works/01-hero-awareness.jpg",
  fourStages: "/images/how-it-works/02-four-stages.jpg",
  journey: "/images/how-it-works/03-customer-journey.jpg",
  whatIs: "/images/how-it-works/04-what-mdd-is.jpg",
  whatIsNot: "/images/how-it-works/05-what-mdd-is-not.jpg",
  trust: "/images/how-it-works/06-trust.jpg",
  simplicity: "/images/how-it-works/07-simplicity.jpg",
};

const CAROUSEL_ITEMS = [
  { type: 'video', poster: '/images/carousel/v7.jpg', src: '/videos/carousel/v7.mp4', title: 'My Direct Debits Reminder App — Advert' },
  { type: 'youtube', poster: '/images/carousel/yt4.jpg', youtubeId: 'QzlB-d6gmF4', title: 'My Direct Debits — Quick walkthrough' },
  { type: 'youtube', poster: '/images/carousel/yt1.jpg', youtubeId: 'yRv3DqQlnHk', title: 'Testimonial — Real user review' },
  { type: 'youtube', poster: '/images/carousel/yt2.jpg', youtubeId: 'cpkXfhyRSCw', title: 'My Direct Debits — Reminder app demo' },
  { type: 'video', poster: '/images/carousel/v6.jpg', src: '/videos/carousel/v6.mp4', title: 'Get real-time updates about your bills' },
  { type: 'video', poster: '/images/carousel/v1.jpg', src: '/videos/carousel/v1.mp4', title: 'Stop Money Disappearing Mid-Month' },
  { type: 'video', poster: '/images/carousel/v5.jpg', src: '/videos/carousel/v5.mp4', title: 'Never Miss a Payment Again — App Review' },
  { type: 'video', poster: '/images/carousel/v8.jpg', src: '/videos/carousel/v8.mp4', title: 'I Found 3 Hidden Subscriptions Draining My Account' },
  { type: 'video', poster: '/images/carousel/v4.jpg', src: '/videos/carousel/v4.mp4', title: 'How This App Stopped Money Disappearing on Payday' },
  { type: 'video', poster: '/images/carousel/v2.jpg', src: '/videos/carousel/v2.mp4', title: 'How I Took Control of My Finances' },
  { type: 'video', poster: '/images/carousel/v3.jpg', src: '/videos/carousel/v3.mp4', title: "I Didn't Know Where My Money Was Going" },
  { type: 'image', poster: '/images/carousel/i3.jpg', src: '/images/carousel/full/i3.jpg', title: 'Your bank tells you AFTER. We tell you BEFORE.' },
  { type: 'image', poster: '/images/carousel/i4.jpg', src: '/images/carousel/full/i4.jpg', title: 'Stay in control of your money' },
  { type: 'youtube', poster: '/images/carousel/yt3.jpg', youtubeId: 'nSWxD2gbJBs', title: 'Need help remembering all those bill dates?' },
  { type: 'image', poster: '/images/carousel/i6.jpg', src: '/images/carousel/full/i6.jpg', title: 'Keep forgetting your bill dates?' },
  { type: 'image', poster: '/images/carousel/i7.jpg', src: '/images/carousel/full/i7.jpg', title: 'Looking for a simple bills reminder?' },
  { type: 'image', poster: '/images/carousel/i5.jpg', src: '/images/carousel/full/i5.jpg', title: 'Still forgetting your bill dates?' },
  { type: 'image', poster: '/images/carousel/i1.jpg', src: '/images/carousel/full/i1.jpg', title: 'Your bank tells you AFTER. We tell you BEFORE.' },
  { type: 'image', poster: '/images/carousel/i2.jpg', src: '/images/carousel/full/i2.jpg', title: 'My Direct Debits — See everything. Stay in control.' },
];

function Step({ number, title, children }) {
  return (
    <div className="flex gap-4 sm:gap-6" data-testid={`how-step-${number}`}>
      <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary text-white font-heading font-bold text-lg sm:text-xl flex items-center justify-center shadow-md">
        {number}
      </div>
      <div className="flex-1">
        <h3 className="font-heading font-semibold text-dark text-lg sm:text-xl mb-1">{title}</h3>
        <p className="text-text-secondary text-sm sm:text-base leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function FeatureItem({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-border shadow-sm hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center mb-3">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h4 className="font-heading font-semibold text-dark text-base mb-1">{title}</h4>
      <p className="text-text-secondary text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

function CrossItem({ title, desc }) {
  return (
    <div className="flex gap-3 bg-white rounded-xl p-4 border border-border">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
        <X className="w-4 h-4 text-red-600" />
      </div>
      <div>
        <h4 className="font-heading font-semibold text-dark text-base mb-0.5">{title}</h4>
        <p className="text-text-secondary text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function CheckItem({ title, desc }) {
  return (
    <div className="flex gap-3 bg-primary-light/40 rounded-xl p-4 border border-primary/20">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center mt-0.5">
        <Check className="w-4 h-4 text-white" />
      </div>
      <div>
        <h4 className="font-heading font-semibold text-dark text-base mb-0.5">{title}</h4>
        <p className="text-text-secondary text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function HowItWorksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const ctaTarget = user ? '/dashboard' : '/auth';

  useEffect(() => {
    trackLandingVisit('/how-it-works');
  }, []);

  return (
    <div className="min-h-screen bg-page">
      <SEO
        title="How MDD Works & Keeps You Informed | My Direct Debits"
        description="See how My Direct Debits helps you stay informed about upcoming payments with simple reminders, complete privacy, and no bank connection required."
        path="/how-it-works"
      />

      {/* Header */}
      <header className="glass-header px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="how-it-works-logo">
            <img src={LOGO_URL} alt="My Direct Debits" className="h-10 w-10 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-dark text-base sm:text-lg leading-tight">My Direct Debits</h1>
              <p className="text-xs text-text-light">Reminder App</p>
            </div>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/blog" className="hidden sm:inline-flex bg-primary-light text-primary font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-primary hover:text-white transition-colors" data-testid="how-blog-link">Blog</Link>
            {user ? (
              <Link to="/dashboard" data-testid="how-dashboard-btn" className="btn-primary px-4 sm:px-5 py-2 text-sm">Dashboard</Link>
            ) : (
              <Link to="/auth" data-testid="how-signin-btn" className="hidden sm:inline-flex text-primary font-medium hover:text-primary-hover transition-colors text-sm sm:text-base">Sign In</Link>
            )}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open-btn"
              className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary-light text-primary hover:bg-primary hover:text-white transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[80] sm:hidden bg-black/50 backdrop-blur-sm animate-fade-in"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-menu-drawer"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0 right-0 left-0 bg-white shadow-xl rounded-b-2xl"
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-border">
              <span className="font-heading font-bold text-dark">Menu</span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                className="w-10 h-10 rounded-full hover:bg-border/40 flex items-center justify-center text-text-secondary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex flex-col py-2">
              <a
                href="https://www.qualiquiz.app/quiz/find-out-in-60-seconds-which-money-management-style-are-you-"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="px-5 py-3 text-dark font-medium hover:bg-primary-light transition-colors flex items-center gap-3"
              >
                <span className="w-2 h-2 rounded-full bg-gold" /> Which Spender Am I? Quiz
              </a>
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="px-5 py-3 text-dark font-medium hover:bg-primary-light transition-colors flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-primary" /> Home
              </Link>
              <Link to="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="px-5 py-3 text-dark font-medium hover:bg-primary-light transition-colors flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-primary" /> How It Works
              </Link>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="px-5 py-3 text-dark font-medium hover:bg-primary-light transition-colors flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-primary" /> Blog
              </Link>
              {user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="px-5 py-3 text-primary font-semibold hover:bg-primary-light transition-colors flex items-center gap-3 border-t border-border">
                  <span className="w-2 h-2 rounded-full bg-primary" /> Go to Dashboard
                </Link>
              ) : (
                <Link to="/auth" onClick={() => setMobileMenuOpen(false)} className="px-5 py-3 text-primary font-semibold hover:bg-primary-light transition-colors flex items-center gap-3 border-t border-border">
                  <span className="w-2 h-2 rounded-full bg-primary" /> Sign In
                </Link>
              )}
            </nav>
          </div>
        </div>
      )}
      {/* SECTION 1 — HERO */}
      <section className="px-4 py-10 md:py-16">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-heading font-bold text-dark text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-5">
            Step by Step how MDDReminder Works
          </h2>
          <p className="text-text-secondary text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-8 leading-relaxed">
            MDD helps you stay informed about your upcoming payments by sending reminders through your calendar and smartwatch — before money leaves your account.
          </p>

          {/* YouTube Short — 60-second intro */}
          <div className="mt-2 mx-auto w-full max-w-xs sm:max-w-sm" data-testid="how-hero-short">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-black" style={{ paddingTop: '177.78%' }}>
              <iframe
                src="https://www.youtube.com/embed/uCt3DJcRAX8?rel=0"
                title="My Direct Debits — 60-second intro"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>

          {/* Pilot invitation banner — links to /auth */}
          <Link
            to="/auth"
            data-testid="how-pilot-banner-link"
            aria-label="Join the pilot programme — get your free report"
            className="mt-10 md:mt-14 block rounded-2xl overflow-hidden shadow-lg border border-border hover:shadow-xl transition-shadow group"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/v05tjkiv_Inviting%2010%20people%20to%20join%20pilot%20mddreminder%201.png"
              alt="Got caught out again by a forgotten subscription? Invitation 10 people only — Get my free report"
              className="block w-full h-auto transition-transform duration-300 group-hover:scale-[1.01]"
              data-testid="how-pilot-invitation-image"
              loading="lazy"
            />
          </Link>

          {/* Step-by-step "How to Join Pilot" infographic */}
          <div
            className="mt-6 md:mt-8 rounded-2xl overflow-hidden shadow-lg border border-border bg-white"
            data-testid="how-pilot-steps-section"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_track-bills-app/artifacts/mn946a9j_Step%20by%20step%20how%20to%20join%20pilot.jpg"
              alt="How to Join Pilot — Step 1 Sign Up to Pilot, Step 2 Add at least 3 Debits, Step 3 Go to Settings, Step 4 Share Your Feedback"
              className="block w-full h-auto"
              data-testid="how-pilot-steps-image"
              loading="lazy"
            />
          </div>

          <div className="mt-10 md:mt-14 rounded-2xl overflow-hidden shadow-lg border border-border bg-white">
            <img
              src={IMG.hero}
              alt="MDD — Awareness before it happens. Track business and personal accounts in one place."
              className="w-full h-auto block"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2 — HOW IT WORKS */}
      <section className="px-4 py-10 md:py-16 bg-white border-y border-border">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-heading font-bold text-dark text-3xl sm:text-4xl md:text-5xl mb-3">How It Works</h2>
            <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
              Four simple steps. Total control. No technical knowledge needed.
            </p>
          </div>

          <div className="max-w-3xl mx-auto mb-10 md:mb-14" data-testid="how-it-works-video">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-black" style={{ paddingTop: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/ZVlZ4aOenGc?rel=0"
                title="How My Direct Debits Works"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-md border border-border mb-10 md:mb-14">
            <img src={IMG.fourStages} alt="Four simple stages: Get Started, Add a Direct Debit, Set Reminders, Stay in Control" className="w-full h-auto block" loading="lazy" />
          </div>

          <div className="grid sm:grid-cols-2 gap-6 md:gap-8 mb-12">
            <Step number="1" title="Sign Up">Create your account using your email and password. No card details required.</Step>
            <Step number="2" title="Add Your Payments">Enter the name, amount and date of your direct debits, subscriptions or standing orders.</Step>
            <Step number="3" title="Set Your Reminders">Choose when you want to be reminded — for example 7, 5 or 1 day before each payment.</Step>
            <Step number="4" title="Stay Informed">Receive reminders through your calendar, phone and smart watch so you know what's coming before it happens.</Step>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-md border border-border">
            <img src={IMG.journey} alt="The customer journey — simple, frictionless, no technical knowledge required" className="w-full h-auto block" loading="lazy" />
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHAT MDD DOES */}
      <section className="px-4 py-10 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-primary font-semibold text-xs sm:text-sm tracking-wider uppercase mb-2">Why MDD does not require FCA authorisation</p>
            <h2 className="font-heading font-bold text-dark text-3xl sm:text-4xl md:text-5xl mb-4">Built as an information and reminder service</h2>
            <p className="text-text-secondary text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
              MDD is designed to help customers stay informed about their upcoming payments. It is a reminder and summary tool — not a budgeting or planning tool — and does not provide financial advice.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-md border border-border bg-white mb-10">
            <img src={IMG.whatIs} alt="What MDD is — reminders, summary, visibility, user-controlled" className="w-full h-auto block" loading="lazy" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            <FeatureItem icon={Bell} title="Sends reminders" desc="Alerts you before payments are due, through your calendar and device notifications." />
            <FeatureItem icon={FileText} title="Clear summary" desc="Shows your upcoming payments in one simple, easy-to-read place." />
            <FeatureItem icon={Eye} title="Full visibility" desc="See your outgoing commitments at a glance and stay organised and prepared." />
            <FeatureItem icon={User} title="User-controlled" desc="You add and manage your own information. Nothing is connected to your bank." />
            <FeatureItem icon={ShieldCheck} title="Information-only service" desc="Operates outside the scope of regulated financial activity. No advice, no processing." />
            <FeatureItem icon={Lock} title="Privacy by design" desc="We collect only what's needed to give you reminders and summaries — nothing more." />
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT MDD DOES NOT DO */}
      <section className="px-4 py-10 md:py-16 bg-white border-y border-border">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-dark text-3xl sm:text-4xl md:text-5xl mb-3">What MDD Does NOT Do</h2>
            <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
              MDD simply reminds you based on the information you choose to enter.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-md border border-border mb-10">
            <img src={IMG.whatIsNot} alt="What MDD is not — no bank access, no payment processing, no lending, no financial advice" className="w-full h-auto block" loading="lazy" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3 md:gap-4 mb-8">
            <CrossItem title="Not a budgeting or planning tool" desc="MDD doesn't help you create budgets, set goals or plan spending." />
            <CrossItem title="No access to bank accounts" desc="MDD never connects to or views your bank accounts or balances." />
            <CrossItem title="No payment processing" desc="MDD doesn't move money or process any payments on your behalf." />
            <CrossItem title="No lending or credit" desc="MDD doesn't offer credit, loans, or broker financial products." />
            <CrossItem title="No financial advice" desc="MDD doesn't provide personalised financial advice or recommendations." />
            <CrossItem title="No transaction monitoring" desc="MDD doesn't track or analyse what's happening in your accounts." />
          </div>

          <div className="grid sm:grid-cols-3 gap-3 md:gap-4">
            <CheckItem title="Outside regulated activity" desc="Information and reminders only — no regulated financial activity." />
            <CheckItem title="Simple by design" desc="Low regulatory complexity. Focused on reminders and summaries." />
            <CheckItem title="Privacy first" desc="Secure by design. You stay in control of your own information." />
          </div>
        </div>
      </section>

      {/* SECTION 5 — SAFETY & TRUST */}
      <section className="px-4 py-10 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-dark text-3xl sm:text-4xl md:text-5xl mb-3">Designed for Simplicity and Trust</h2>
            <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
              Your data. Your control. Our priority.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-md border border-border bg-white mb-10">
            <img src={IMG.trust} alt="Why customers can trust MDD — encryption, privacy by design, GDPR-conscious, no bank access" className="w-full h-auto block" loading="lazy" />
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-5 mb-8">
            <FeatureItem icon={User} title="User-controlled data" desc="You choose what to add, edit or delete. We never collect what we don't need." />
            <FeatureItem icon={Database} title="No bank connection" desc="MDD never connects to or accesses your bank accounts or financial data." />
            <FeatureItem icon={Lock} title="Encrypted in transit" desc="Connections use modern TLS 1.2+ encryption to keep your data secure." />
            <FeatureItem icon={ShieldCheck} title="GDPR-conscious" desc="Transparent about how data is used. You can request, edit or delete it any time." />
            <FeatureItem icon={FileText} title="Minimum data only" desc="We only store what's required to send your reminders and summaries." />
            <FeatureItem icon={Eye} title="Clear and transparent" desc="No hidden tricks. Plain-English terms and a straightforward privacy policy." />
          </div>

          <div className="bg-dark text-white rounded-2xl p-6 md:p-8 text-center">
            <p className="text-base sm:text-lg md:text-xl font-heading font-medium leading-relaxed">
              We do not sell, share or access your financial data. <span className="text-primary-light">Your trust is our foundation — we protect it every day.</span>
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 6 — MEDIA CAROUSEL (videos & images) */}
      <section className="px-4 py-10 md:py-16 bg-white border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-heading font-bold text-dark text-3xl sm:text-4xl md:text-5xl mb-3">See MDD in action</h2>
            <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
              Real stories. Real reminders. Tap any card to play or expand.
            </p>
          </div>
          <div className="px-2 sm:px-6">
            <MediaCarousel items={CAROUSEL_ITEMS} />
          </div>
        </div>
      </section>

      {/* SECTION 7 — FINAL CTA */}
      <section className="px-4 py-10 md:py-16 bg-white border-t border-border">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-md border border-border mb-10">
            <img src={IMG.simplicity} alt="Simplicity in every step — sign up, add, stay in control" className="w-full h-auto block" loading="lazy" />
          </div>

          <div className="text-center">
            <h2 className="font-heading font-bold text-dark text-3xl sm:text-4xl md:text-5xl mb-3">Start free today</h2>
            <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto mb-8">
              Everything you need. Nothing you don't. MDD keeps managing direct debits simple — so you can focus on what matters.
            </p>
            <button
              onClick={() => navigate(ctaTarget)}
              data-testid="how-cta-final-btn"
              className="btn-primary px-8 py-3 text-base sm:text-lg"
            >
              Create Your Free Account
            </button>
            <p className="text-xs sm:text-sm text-text-light mt-4">Takes less than 30 seconds. No card required.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 bg-dark">
        <div className="max-w-5xl mx-auto text-center">
          <img src={LOGO_URL} alt="My Direct Debits" className="h-12 w-12 mx-auto mb-4 opacity-80" />
          <p className="text-white/60 text-sm mb-4">A product by Voliciti Group Ventures Ltd</p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/" className="text-white/60 hover:text-white transition-colors">Home</Link>
            <Link to="/how-it-works" className="text-white/60 hover:text-white transition-colors">How It Works</Link>
            <Link to="/blog" className="text-white/60 hover:text-white transition-colors">Blog</Link>
            <Link to="/privacy" className="text-white/60 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-white/60 hover:text-white transition-colors">Terms</Link>
          </div>
          <div className="mt-4">
            <SocialLinks variant="dark" />
          </div>
        </div>
      </footer>
    </div>
  );
}
